// lib/bottleSync.js
// =============================================================================
// Bridges the Intelligent WTR Bottle's live telemetry to the Verified Intake
// Event pipeline. Detects drinking episodes from settled water-level movement
// while the bottle is upright, measures each episode as the NET level change
// between its opening and closing settled states, posts one event per episode
// to /api/wtr-bottle/event, and reflects consumed volume locally.
//
// WHY NET MEASUREMENT (scale ground truth, two runs, 2026-07-07):
//   The post-drink surface settles by OSCILLATING (slosh decay), not by
//   monotonic descent. Summing settled steps gets it wrong in both possible
//   ways, and both were observed against a kitchen scale:
//     Run 1: 93 g drunk, 65 mL posted. Sub-threshold descent steps were
//            absorbed by the anti-jitter clause (undercount).
//     Run 2: 86 g drunk, 109 mL posted. Counting sub-threshold drops while
//            forgiving sub-threshold rises rectified the slosh (overcount).
//   In BOTH runs, (opening settled level - closing settled level) * 3.1
//   equals the scale number. The net is honest; step-summing was the bug.
//   Episodes are therefore measured endpoint-to-endpoint. A side benefit:
//   any artifact that drops and fully recovers within an episode nets to
//   zero and posts nothing.
//
// FIRMWARE REALITY (PP02 SMART 1, fw=10, raw-frame diagnosis 2026-07-07; see
// the Production Partners defect report of the same date):
//   waterLevel (0x04) = ToF water column height in mm. The only trustworthy
//                       volume signal. Garbage modes handled: dropout frames
//                       zeroed inside checksum-valid frames (gated by
//                       validLevel; posted a false 287 mL intake before
//                       gating) and tilt plateaus from drinking posture
//                       (gated by uprightFrame via pitch; posted a false
//                       707 mL empty before gating).
//   pitch (0x16)      = bottle attitude, 90 to 91 upright at rest. Gate input.
//   volume (0x06)     = DEAD (570 empty, 395 on 250 mL, then 16/53/71/638 on
//                       untouched fills). Stored as cumulative_ml for
//                       reference only. Do not chase its occasional
//                       coincidental resemblance to real values.
//   intakePct (0x07)  = BROKEN counter (creeps sealed, ignores real sips). Ignored.
//   waterTemp (0x05)  = whole degrees C, violating the V05 deci-degree spec.
//                       Stored value is wrong 10x pending the firmware fix.
//   tds (0x02)        = only reads near full. Last non-zero reading is stamped
//                       on drinks.
//
// SCALE-DEBT (named): mL conversion is client-side. Events post level_delta_mm
// and ml_per_mm_used (route + columns landed 2026-07-07) so the server can
// recompute or take over conversion server-side before the native iOS build.
// =============================================================================

import { authHeaders } from './auth';

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';

// Per-SKU calibration. SMART 1 measured 2026-07-07 against a graduated 250 mL
// pour and validated by two kitchen-scale sips (93 g and 86 g) at two fill
// levels: slope 3.1 mL per mm, empty ToF offset 17 mm, full level ~225 mm.
const CAL = {
  SMART1: {
    ML_PER_MM: 3.1,
    EMPTY_OFFSET_MM: 17,
    MAX_LEVEL_MM: 260,
  },
};
const SKU = CAL.SMART1;

// Detection thresholds.
const JITTER_MM       = 3;     // readings within this of each other hold still
const SETTLE_FRAMES   = 3;     // consecutive still upright frames before trust
const DROP_MIN_MM     = 6;     // settled drop that can OPEN a drinking episode;
                               // also the minimum NET drop an episode must show
                               // at flush to post (a fully recovered artifact
                               // nets under this and is discarded)
const RISE_MIN_MM     = 12;    // settled rise that closes any episode as a refill
const MAX_SIP_ML      = 300;   // a single settled step larger than this is a
                               // pour or dump ('empty'), never intake
const SESSION_GAP_MS  = 60000; // frame gap beyond this resets the baseline

// Orientation gate. Pitch reads 90 to 91 on an upright resting bottle.
const UPRIGHT_PITCH_DEG = 90;
const PITCH_TOLERANCE   = 8;

// Episode timing. Settling steps were observed 16 to 23s apart; the quiet
// window comfortably exceeds that. The ceiling force-flushes a continuously
// sipped episode so an event always lands within bounded time.
const INTAKE_QUIET_MS   = 45000;
const MAX_EPISODE_MS    = 300000;

// TDS memory: last non-zero reading is the current water's quality.
let lastValidTds = null;

function num(v) { const n = parseFloat(v); return Number.isFinite(n) ? n : null; }

function sv(reading, key) {
  if (!reading || !reading.sensors || !reading.sensors[key]) return null;
  const s = reading.sensors[key];
  return num(s.val != null ? s.val : s.raw);
}

export function readProfileEmail() {
  try { const p = JSON.parse(localStorage.getItem('wtr_profile') || '{}'); return (p.email || '').trim().toLowerCase() || null; }
  catch (e) { return null; }
}

export function readHomeHubDevice() {
  try { return JSON.parse(localStorage.getItem('wtr_profile') || '{}').deviceId || null; }
  catch (e) { return null; }
}

export function readGeo(reading) {
  const lat = sv(reading, 'lat');
  const lng = sv(reading, 'lng');
  if (lat == null || lng == null) return { latitude: null, longitude: null, geo_accuracy_m: null };
  return { latitude: lat, longitude: lng, geo_accuracy_m: sv(reading, 'geoAcc') };
}

// Level input gate: rejects firmware dropout zeros and impossible readings.
function validLevel(mm) {
  if (mm == null) return false;
  if (mm <= 0) return false;               // dropout frame; a truly empty bottle
                                           // reads ~EMPTY_OFFSET_MM, never 0
  if (mm > SKU.MAX_LEVEL_MM) return false; // slosh spike or garbage
  return true;
}

// Orientation gate: level readings only count while the bottle is upright.
function uprightFrame(reading) {
  const pitch = sv(reading, 'pitch');
  if (pitch == null) return false;
  return Math.abs(pitch - UPRIGHT_PITCH_DEG) <= PITCH_TOLERANCE;
}

// Settle-gated detector state.
let pendingLevel = null;
let pendingCount = 0;
let settledLevel = null;
let lastFrameTs  = 0;

// Episode state: measured endpoint-to-endpoint, never by summing steps.
let episodeOpen       = false;
let episodeStartLevel = null;   // settled level the moment the episode opened
let episodeStartTs    = 0;
let episodeCtx        = null;   // latest telemetry context for the payload
let episodeTimer      = null;

export function resetDetector() {
  pendingLevel = null; pendingCount = 0; settledLevel = null; lastFrameTs = 0;
}

function clearEpisode() {
  episodeOpen = false; episodeStartLevel = null; episodeStartTs = 0; episodeCtx = null;
  if (episodeTimer) { clearTimeout(episodeTimer); episodeTimer = null; }
}

// Feed one telemetry reading. Emits settled-state transitions; the episode
// logic in syncTelemetry decides what posts.
//   null
//   { kind:'drop',  from, to, delta_mm }   settled drop of DROP_MIN_MM or more
//   { kind:'pour',  from, to, delta_mm }   settled drop too large to be a sip
//   { kind:'rise',  from, to, delta_mm }   settled rise of RISE_MIN_MM or more
//   { kind:'drift', from, to, delta_mm }   sub-threshold settled movement, signed
export function detectEvent(prev, cur) {
  const now = Date.now();
  if (lastFrameTs && (now - lastFrameTs) > SESSION_GAP_MS) {
    resetDetector();   // a long gap means the old baseline belongs to another
                       // session; never fabricate an event across it
  }
  lastFrameTs = now;

  if (!uprightFrame(cur)) return null;   // tilted frames are invisible

  const curLvl = sv(cur, 'waterLevel');
  if (!validLevel(curLvl)) return null;  // dropouts and garbage never enter

  if (pendingLevel != null && Math.abs(curLvl - pendingLevel) <= JITTER_MM) {
    pendingCount += 1;
  } else {
    pendingLevel = curLvl;
    pendingCount = 1;
  }
  if (pendingCount < SETTLE_FRAMES) return null;

  if (settledLevel == null) {
    settledLevel = pendingLevel;
    return null;
  }

  const from = settledLevel;
  const to   = pendingLevel;
  const deltaMm = from - to;   // positive = water left
  if (deltaMm === 0) return null;
  settledLevel = to;

  if (deltaMm >= DROP_MIN_MM) {
    const ml = Math.round(deltaMm * SKU.ML_PER_MM);
    if (ml > MAX_SIP_ML) return { kind: 'pour', from, to, delta_mm: deltaMm };
    return { kind: 'drop', from, to, delta_mm: deltaMm };
  }
  if (-deltaMm >= RISE_MIN_MM) return { kind: 'rise', from, to, delta_mm: deltaMm };
  return { kind: 'drift', from, to, delta_mm: deltaMm };
}

export function writeLocalIntake(volumeMl, filtered) {
  try {
    const intake = JSON.parse(localStorage.getItem('wtr_intake') || '{}');
    const bucket = filtered ? 'hub' : 'tap';
    const liters = (parseFloat(intake[bucket]) || 0) + (volumeMl / 1000);
    intake[bucket] = String(Math.round(liters * 100) / 100);
    localStorage.setItem('wtr_intake', JSON.stringify(intake));
  } catch (e) {}
}

export async function postEvent(payload) {
  try {
    const r = await fetch(`${API_BASE}/api/wtr-bottle/event`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
    if (!r.ok) return null;
    return await r.json();
  } catch (e) { return null; }
}

function buildPayload({ cur, deviceId, sourceHubDevice }, eventType, volumeMl, deltaMm) {
  const liveTds = sv(cur, 'tds');
  const tdsForEvent = (liveTds != null && liveTds > 0) ? liveTds : lastValidTds;
  const geo = readGeo(cur);
  return {
    device_id: deviceId,
    user_email: readProfileEmail(),
    event_type: eventType,
    volume_ml: volumeMl != null ? volumeMl : null,
    level_delta_mm: deltaMm != null ? deltaMm : null,
    ml_per_mm_used: SKU.ML_PER_MM,
    cumulative_ml: sv(cur, 'volume'),   // reference only; firmware field is defective
    tds_ppm: tdsForEvent,
    water_temp_c: sv(cur, 'waterTemp'), // known wrong 10x pending firmware fix
    ph: sv(cur, 'ph'),
    raw_sensors: cur.sensors || null,
    latitude: geo.latitude,
    longitude: geo.longitude,
    geo_accuracy_m: geo.geo_accuracy_m,
    source_hub_device: eventType === 'fill' ? (sourceHubDevice || null) : null,
  };
}

// Flush the open episode as ONE intake event measured endpoint-to-endpoint:
// net mm = settled level at open minus settled level now. A net below
// DROP_MIN_MM means whatever dropped came back (slosh or a recovered
// artifact); nothing posts.
export async function flushPendingIntake() {
  if (!episodeOpen || episodeStartLevel == null || settledLevel == null || !episodeCtx) {
    clearEpisode();
    return null;
  }
  const netMm = episodeStartLevel - settledLevel;
  const ctx = episodeCtx;
  clearEpisode();

  if (netMm < DROP_MIN_MM) return null;
  const ml = Math.round(netMm * SKU.ML_PER_MM);
  const res = await postEvent(buildPayload(ctx, 'intake', ml, netMm));
  const filtered = !!(res && res.source && res.source.filtered);
  writeLocalIntake(ml, filtered);
  return res;
}

function scheduleFlush() {
  if (episodeTimer) clearTimeout(episodeTimer);
  const now = Date.now();
  const sinceStart = episodeStartTs ? (now - episodeStartTs) : 0;
  const wait = Math.max(0, Math.min(INTAKE_QUIET_MS, MAX_EPISODE_MS - sinceStart));
  episodeTimer = setTimeout(() => { flushPendingIntake(); }, wait);
}

// Process one telemetry transition end to end. A qualifying drop opens an
// episode anchored at its FROM level; all settled movement while the episode
// is open (drops, drifts up or down) merely keeps it alive, because the flush
// measures the net. Rises and pours close the episode first.
export async function syncTelemetry({ prev, cur, deviceId, sourceHubDevice }) {
  // Demo isolation: simulated telemetry never reaches the production pipeline
  // (46 demo events, 1756 mL of fake verified intake, purged 2026-07-07).
  if (!deviceId || String(deviceId).toLowerCase().startsWith('demo')) return null;

  const liveTds = sv(cur, 'tds');
  if (liveTds != null && liveTds > 0) lastValidTds = liveTds;

  const evt = detectEvent(prev, cur);
  if (!evt) return null;

  if (evt.kind === 'drop') {
    if (!episodeOpen) {
      episodeOpen = true;
      episodeStartLevel = evt.from;   // anchor at the level BEFORE the drop
      episodeStartTs = Date.now();
    }
    episodeCtx = { cur, deviceId, sourceHubDevice };
    scheduleFlush();
    return null;
  }

  if (evt.kind === 'drift') {
    if (episodeOpen) {
      // Settling activity in either direction: the net at flush will account
      // for it exactly. Keep the context fresh and the quiet window open.
      episodeCtx = { cur, deviceId, sourceHubDevice };
      scheduleFlush();
    }
    // No open episode: idle jitter, absorbed. The baseline already advanced
    // in detectEvent, so it can never accumulate.
    return null;
  }

  if (evt.kind === 'rise') {
    // Refill: close any drinking episode first (net measured up to the last
    // settled level before the rise), then post the fill.
    await flushPendingIntake();
    const payload = buildPayload({ cur, deviceId, sourceHubDevice }, 'fill', null, evt.delta_mm);
    return await postEvent(payload);
  }

  // pour: a settled drop too large to be a sip. Close the episode, then post.
  await flushPendingIntake();
  const ml = Math.round(evt.delta_mm * SKU.ML_PER_MM);
  const payload = buildPayload({ cur, deviceId, sourceHubDevice }, 'empty', ml, evt.delta_mm);
  return await postEvent(payload);
}
